import once from 'lodash/once';
import { oneOf } from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';

import api from 'atg/api/API';
import betReducer from 'atg/redux/reducers/betReducer';
import couponReducer from 'atg/redux/reducers/couponReducer';
import jokerWizReducer from 'atg/redux/reducers/jokerWizReducer';
import stompReducer from 'atg/redux/reducers/stompReducer';
import viewReducer from 'atg/redux/reducers/viewReducer';
import templBottomConditionsABCD from 'atg/templates/bottom-conditions-abcd-reduction';
import templBottomConditionsBaseHorse from 'atg/templates/bottom-conditions-base-horse-reduction';
import templBottomConditionsDividend from 'atg/templates/bottom-conditions-dividend-reduction';
import templBottomConditionsJockeyTrainer from 'atg/templates/bottom-conditions-jockey-trainer-reduction';
import templBottomConditionsOwnWinPercent from 'atg/templates/bottom-conditions-own-profit-percentage';
import templBottomConditionsPoint from 'atg/templates/bottom-conditions-point-reduction';
import templBottomConditionsRaceSelectorItem from 'atg/templates/bottom-conditions-race-selector-item';
import templBottomConditionsRanking from 'atg/templates/bottom-conditions-ranking';
import templBottomConditionsStartNumber from 'atg/templates/bottom-conditions-start-number-reduction';
import templBottomConditionsTrend from 'atg/templates/bottom-conditions-trend-reduction';
import templBottomInfo from 'atg/templates/bottom-info';
import templDialogExport from 'atg/templates/dialog-export';
import templDialogExportNorway from 'atg/templates/dialog-export-norway';
import templGrid from 'atg/templates/grid';
import templHorseInfo from 'atg/templates/horse-info';
import templOwnProfitContent from 'atg/templates/own-profit-content';
import templOwnProfitEvaluation from 'atg/templates/own-profit-evaluation';
import templPopupInfo from 'atg/templates/popup-info';
import templPopupMenu from 'atg/templates/popup-menu';
import templRaceAnalysis from 'atg/templates/race-analysis';
import Request from 'atg/utils/Request';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import withAsyncReducers from 'shared/containers/withAsyncReducers';
import {
  showJokerWizDialog,
  unAuthorizedRequestError,
} from 'shared/redux/actions';
import { user } from 'shared/redux/selectors';

import * as joker from '../Joker';
import BetRouteContainer from './BetRouteContainer';
import CreateBet from './CreateBetContainer';
import Dialogs from './Dialogs';

const setup = once(() => {
  StickyTemplate.loadHTML('popup-menu', templPopupMenu);
  StickyTemplate.loadHTML('popup-info', templPopupInfo);
  StickyTemplate.loadHTML('grid', templGrid);
  StickyTemplate.loadHTML('bottom-info', templBottomInfo);
  if (IS_SE_SITE) {
    StickyTemplate.loadHTML('dialog-export', templDialogExport);
  } else {
    StickyTemplate.loadHTML('dialog-export-norway', templDialogExportNorway);
  }
  StickyTemplate.loadHTML('horse-info', templHorseInfo);
  StickyTemplate.loadHTML('race-analysis', templRaceAnalysis);
  StickyTemplate.loadHTML('own-profit-evaluation', templOwnProfitEvaluation);
  StickyTemplate.loadHTML('own-profit-content', templOwnProfitContent);
  StickyTemplate.loadHTML(
    'bottom-conditions-ranking',
    templBottomConditionsRanking,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-start-number-reduction',
    templBottomConditionsStartNumber,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-point-reduction',
    templBottomConditionsPoint,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-race-selector-item',
    templBottomConditionsRaceSelectorItem,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-abcd-reduction',
    templBottomConditionsABCD,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-base-horse-reduction',
    templBottomConditionsBaseHorse,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-jockey-trainer-reduction',
    templBottomConditionsJockeyTrainer,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-dividend-reduction',
    templBottomConditionsDividend,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-trend-reduction',
    templBottomConditionsTrend,
  );
  StickyTemplate.loadHTML(
    'bottom-conditions-own-profit-percent',
    templBottomConditionsOwnWinPercent,
  );
});

let instanceCounter = 0;
let jokerInitialized = false;

export default compose(
  withAsyncReducers({
    atg__bet: betReducer,
    atg__coupons: couponReducer,
    atg__jokerwiz: jokerWizReducer,
    atg__stomp: stompReducer,
    atg__view: viewReducer,
  }),
  withRouter,
  connect(
    state => ({
      hasSubscription: user.hasSubscription(state),
    }),
    dispatch =>
      bindActionCreators(
        {
          showJokerWizDialog,
          unAuthorizedRequestError,
        },
        dispatch,
      ),
  ),
)(
  class AtgJoker extends Component {
    static propTypes = {
      entryPoint: oneOf(['route', 'create', 'jokerwiz', 'dialogs']).isRequired,
    };

    state = {
      initialized: jokerInitialized,
    };

    constructor() {
      super();
      setup();
    }

    componentDidMount() {
      instanceCounter++;
      if (!jokerInitialized) {
        const jokerController = joker.setup(api, this.props.history);
        Request.setDefaultErrorHandler(request => {
          jokerController.onAPIRequestError(request);
        });
        Request.setUnauthorizedHandler(() => {
          this.props.unAuthorizedRequestError();
        });
        api.setup(jokerController);
        jokerInitialized = true;
        this.setState({
          initialized: true,
        });
      }

      if (this.props.entryPoint === 'jokerwiz') {
        this.props.showJokerWizDialog(this.props.betParams);
      }
    }

    componentWillUnmount() {
      instanceCounter--;
      // Wait one frame because we may transition back in to atg joker,
      // e.g. when creating a new bet.
      setTimeout(() => {
        if (instanceCounter === 0) {
          Request.setDefaultErrorHandler();
          Request.setUnauthorizedHandler();
          joker.teardown();
          api.teardown();
          jokerInitialized = false;
        }
      }, 0);
    }

    render() {
      if (!this.state.initialized) {
        return null;
      }

      const { entryPoint, ...props } = this.props;

      const childrenProps = {
        ...props,
        jokerController: joker.getInstance(),
      };

      if (entryPoint === 'dialogs' || entryPoint === 'jokerwiz') {
        return (
          <ErrorBoundary>
            <Dialogs {...childrenProps} />
          </ErrorBoundary>
        );
      }

      if (entryPoint === 'create') {
        return (
          <ErrorBoundary>
            <CreateBet {...childrenProps} />
          </ErrorBoundary>
        );
      }

      return (
        <ErrorBoundary>
          <BetRouteContainer {...childrenProps} />
        </ErrorBoundary>
      );
    }
  },
);
